/**
 * CompuMe AI Agent — Frontend v3.0
 * Asset: compume-ai.js
 *
 * Improvements over v2:
 *  - Recommendation cards: hero layout for Best Overall + secondary row for
 *    Best Value / Best Performance / Most Portable (reads recommendation_cards from API)
 *  - Comparison table renderer — triggered automatically on vs. queries
 *  - Tradeoff pills shown after recommendations
 *  - Word-by-word typewriter effect for AI responses
 *  - AbortController — cancels in-flight request when user sends a new one
 *  - Score-reason badges on product cards (💰 Sweet Spot, 🎯 Perfect Match, etc.)
 *  - Dynamic follow-up chips driven by API intent + memory
 *  - ESC key and outside-click to close panel
 *  - Improved markdown: h3/h4, ordered lists, inline code, horizontal rules
 *  - Aria-live region for screen-reader announcements
 *  - Inventory-weak fallback rendering (shows widened-search results gracefully)
 *  - Smooth entrance animation on product cards (staggered)
 *  - View-on-site button for each product card
 */

(function () {
  'use strict';

  // ─── Configuration ──────────────────────────────────────────────────────────
  var WORKER_URL = 'https://compume-ai.bilal-894.workers.dev';
  var WHATSAPP   = 'https://wa.me/962798000180';
  var LS_KEY     = 'cm_chat_v3';  // bumped from v2 to reset old state cleanly
  var LS_TTL     = 24 * 60 * 60 * 1000; // 24 h
  var TW_SPEED   = 20; // ms per word — adaptive, clamped in typewrite()

  // ─── State ──────────────────────────────────────────────────────────────────
  var CM_MSGS      = [];
  var CM_BUSY      = false;
  var CM_OPEN      = false;
  var CM_ERRORS    = 0;
  var CM_LAST_SEND = 0;
  var CM_MEMORY    = {};    // latest memory snapshot from API response
  var CM_INTENT    = null;  // latest intent from API response

  // ─── Ella theme stacking fix ─────────────────────────────────────────────────
  function escapeStacking() {
    ['cm-fab', 'cm-panel'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && el.parentNode !== document.body) document.body.appendChild(el);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', escapeStacking);
  } else {
    escapeStacking();
  }

  // ─── Utilities ───────────────────────────────────────────────────────────────
  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function ts() {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  function isRTL(text) {
    return /[\u0600-\u06FF\u0750-\u077F]/.test(String(text || '').slice(0, 80));
  }

  function el(tag, cls, attrs) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (attrs) Object.keys(attrs).forEach(function (k) { e.setAttribute(k, attrs[k]); });
    return e;
  }

  // ─── Improved markdown → HTML ─────────────────────────────────────────────────
  function md(text) {
    var t = esc(String(text || ''));

    // Headers (h3 / h4 only)
    t = t.replace(/^####\s+(.+)$/gm, '<h4 class="cm-h4">$1</h4>');
    t = t.replace(/^###\s+(.+)$/gm,  '<h3 class="cm-h3">$1</h3>');

    // Bold + italic combos
    t = t.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
    t = t.replace(/\*\*(.*?)\*\*/g,     '<strong>$1</strong>');
    t = t.replace(/_(.*?)_/g,           '<em>$1</em>');

    // Inline code
    t = t.replace(/`([^`]+)`/g, '<code class="cm-code">$1</code>');

    // Links
    t = t.replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="cm-link">$1 ↗</a>'
    );

    // Horizontal rule
    t = t.replace(/^---+$/gm, '<hr class="cm-hr">');

    // Process line by line to handle lists
    var lines  = t.split('\n');
    var out    = '';
    var inUL   = false;
    var inOL   = false;

    lines.forEach(function (l) {
      var li  = l.match(/^[\-\*•]\s+(.*)/);
      var num = l.match(/^\d+\.\s+(.*)/);

      if (li) {
        if (inOL) { out += '</ol>'; inOL = false; }
        if (!inUL) { out += '<ul class="cm-ul">'; inUL = true; }
        out += '<li>' + li[1] + '</li>';
      } else if (num) {
        if (inUL) { out += '</ul>'; inUL = false; }
        if (!inOL) { out += '<ol class="cm-ol">'; inOL = true; }
        out += '<li>' + num[1] + '</li>';
      } else {
        if (inUL) { out += '</ul>'; inUL = false; }
        if (inOL) { out += '</ol>'; inOL = false; }
        if (/^<(h[34]|hr)/.test(l)) {
          out += l;
        } else if (l.trim()) {
          out += '<p>' + l + '</p>';
        }
      }
    });
    if (inUL) out += '</ul>';
    if (inOL) out += '</ol>';
    return out;
  }

  // ─── Typewriter effect ────────────────────────────────────────────────────────
  function typewrite(container, htmlText, onComplete) {
    var div = document.createElement('div');
    div.innerHTML = htmlText;
    var plainWords = (div.textContent || '').trim().split(/\s+/).filter(Boolean);
    var count = plainWords.length;

    if (count === 0 || count > 150) {
      container.innerHTML = htmlText;
      if (onComplete) onComplete();
      return;
    }

    var delay  = Math.max(10, Math.min(TW_SPEED, Math.floor(1800 / count)));
    var idx    = 0;
    var timer;

    function tick() {
      idx++;
      container.textContent = plainWords.slice(0, idx).join(' ');
      if (idx < count) {
        timer = setTimeout(tick, delay);
      } else {
        container.innerHTML = htmlText;
        if (onComplete) onComplete();
      }
    }

    container.textContent = '';
    timer = setTimeout(tick, delay);
    container._twTimer = timer;
  }

  // ─── Page context detection ───────────────────────────────────────────────────
  function getPageContext() {
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
        var p = window.ShopifyAnalytics.meta.product;
        return { product: { title: p.title || '', price: p.price ? (p.price / 100).toFixed(3) : '', id: p.id || '' } };
      }
      var titleEl =
        document.querySelector('.product__title h1') ||
        document.querySelector('h1.product-title') ||
        document.querySelector('[data-product-title]') ||
        document.querySelector('.product-single__title');
      var priceEl =
        document.querySelector('.price__current .money') ||
        document.querySelector('[data-price]') ||
        document.querySelector('.product__price .money');
      if (titleEl) {
        return { product: { title: titleEl.textContent.trim(), price: priceEl ? priceEl.textContent.trim() : '' } };
      }
    } catch (e) {}
    return null;
  }

  // ─── localStorage ─────────────────────────────────────────────────────────────
  function saveHistory() {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ msgs: CM_MSGS, ts: Date.now(), memory: CM_MEMORY }));
    } catch (e) {}
  }

  function loadHistory() {
    try {
      var raw  = localStorage.getItem(LS_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (!data || Date.now() - data.ts > LS_TTL) { localStorage.removeItem(LS_KEY); return; }
      CM_MSGS   = Array.isArray(data.msgs) ? data.msgs : [];
      CM_MEMORY = (data.memory && typeof data.memory === 'object') ? data.memory : {};
    } catch (e) { CM_MSGS = []; }
  }

  // ─── DOM helpers ──────────────────────────────────────────────────────────────
  function chat() { return document.getElementById('cm-chat'); }

  // ─── Score reason → badge ─────────────────────────────────────────────────────
  var REASON_BADGES = {
    budget_sweet_spot: { icon: '💰', label: 'Sweet Spot', cls: 'sweet' },
    preferred_brand:   { icon: '⭐', label: 'Your Brand', cls: 'brand' },
    use_case_tag:      { icon: '🎯', label: 'Perfect Match', cls: 'match' },
    gaming_signal:     { icon: '🎮', label: 'Gaming Pick', cls: 'gaming' },
    business_signal:   { icon: '💼', label: 'Business Pick', cls: 'biz' },
    title_match:       { icon: '✓', label: 'Relevant', cls: 'rel' }
  };

  function getReasonBadge(reasons) {
    if (!Array.isArray(reasons)) return null;
    var priority = ['use_case_tag', 'gaming_signal', 'business_signal',
                    'budget_sweet_spot', 'preferred_brand', 'title_match'];
    for (var i = 0; i < priority.length; i++) {
      if (reasons.indexOf(priority[i]) !== -1 && REASON_BADGES[priority[i]]) {
        return REASON_BADGES[priority[i]];
      }
    }
    return null;
  }

  // ─── Product card builder ─────────────────────────────────────────────────────
  function buildProductCard(p, overrideBadgeLabel, overrideBadgeCls, isHero) {
    var card     = el('a', 'cm-pcard' + (isHero ? ' hero' : ''), { href: p.url || '#', target: '_blank', rel: 'noopener noreferrer' });
    var imgWrap  = el('div', 'cm-pimg-wrap');
    var badge    = el('span', 'cm-badge ' + (p.isUSA ? 'usa' : 'local'));
    badge.textContent = p.isUSA ? '🇺🇸 USA Import' : '🇯🇴 Local';

    if (p.image) {
      var img = el('img', 'cm-pimg', { src: p.image, alt: esc(p.title || ''), loading: 'lazy' });
      imgWrap.appendChild(img);
    } else {
      imgWrap.appendChild(el('div', 'cm-pimg-ph'));
    }
    imgWrap.appendChild(badge);

    var reasonBadge = overrideBadgeLabel
      ? { icon: '', label: overrideBadgeLabel, cls: overrideBadgeCls || '' }
      : getReasonBadge(p._reasons);
    if (reasonBadge) {
      var rb = el('span', 'cm-reason-badge ' + reasonBadge.cls);
      rb.textContent = (reasonBadge.icon ? reasonBadge.icon + ' ' : '') + reasonBadge.label;
      imgWrap.appendChild(rb);
    }

    if (!p.available && !p.in_stock) {
      var oos = el('div', 'cm-oos-overlay');
      oos.textContent = 'Out of Stock';
      imgWrap.appendChild(oos);
    }

    card.appendChild(imgWrap);

    var body  = el('div', 'cm-pbody');
    var title = el('div', 'cm-ptitle');
    title.textContent = p.title || '';
    body.appendChild(title);

    if (p.vendor) {
      var vendor = el('div', 'cm-pvendor');
      vendor.textContent = p.vendor;
      body.appendChild(vendor);
    }

    var foot   = el('div', 'cm-pfoot');
    var price  = parseFloat(p.price || 0);
    var priceEl = el('span', 'cm-price');
    priceEl.textContent = price > 0 ? price.toFixed(3) + ' JOD' : '';
    foot.appendChild(priceEl);

    var availEl = el('span', 'cm-avail ' + ((p.available !== undefined ? p.available : p.in_stock) ? 'in' : 'out'));
    availEl.textContent = (p.available !== undefined ? p.available : p.in_stock) ? 'In Stock' : 'Out of Stock';
    foot.appendChild(availEl);
    body.appendChild(foot);

    if (isHero) {
      var cta = el('div', 'cm-pcard-cta');
      cta.textContent = 'View Product →';
      body.appendChild(cta);
    }

    card.appendChild(body);
    return card;
  }

  // ─── Product grid (flat, fallback) ───────────────────────────────────────────
  function renderProductGrid(products, chatEl) {
    if (!products || products.length === 0) return;
    var grid = el('div', 'cm-pgrid');
    products.slice(0, 4).forEach(function (p, i) {
      var card = buildProductCard(p, null, null, false);
      card.style.animationDelay = (i * 60) + 'ms';
      card.classList.add('cm-card-enter');
      grid.appendChild(card);
    });
    chatEl.appendChild(grid);
  }

  // ─── Recommendation cards (structured) ───────────────────────────────────────
  function renderRecommendationCards(cards, chatEl) {
    if (!cards) return;

    if (cards.inventory_warning) {
      var warn = el('div', 'cm-warn-banner');
      warn.innerHTML = '⚠️ No exact matches in stock — showing closest available alternatives';
      chatEl.appendChild(warn);
    }

    if (cards.best_overall) {
      var heroWrap = el('div', 'cm-rec-hero-wrap');
      var heroLabel = el('div', 'cm-rec-section-label');
      heroLabel.innerHTML = '⭐ Best Overall Pick';
      heroWrap.appendChild(heroLabel);

      if (cards.why_overall && cards.why_overall.length > 0) {
        var whyRow = el('div', 'cm-why-row');
        cards.why_overall.slice(0, 3).forEach(function (r) {
          var rb = REASON_BADGES[r];
          if (rb) {
            var chip = el('span', 'cm-why-chip ' + rb.cls);
            chip.textContent = rb.icon + ' ' + rb.label;
            whyRow.appendChild(chip);
          }
        });
        heroWrap.appendChild(whyRow);
      }

      var heroCard = buildProductCard(cards.best_overall, null, null, true);
      heroCard.classList.add('cm-card-enter');
      heroWrap.appendChild(heroCard);
      chatEl.appendChild(heroWrap);
    }

    var secondaries = [];
    if (cards.best_value)      secondaries.push({ p: cards.best_value,       label: '💰 Best Value',      cls: 'value' });
    if (cards.best_performance)secondaries.push({ p: cards.best_performance,  label: '🚀 Best Performance', cls: 'perf'  });
    if (cards.best_portable)   secondaries.push({ p: cards.best_portable,    label: '🎒 Most Portable',   cls: 'port'  });

    if (secondaries.length > 0) {
      var secWrap = el('div', 'cm-rec-sec-wrap');
      var secLabel = el('div', 'cm-rec-section-label small');
      secLabel.textContent = 'Also consider';
      secWrap.appendChild(secLabel);
      var secRow = el('div', 'cm-rec-row');
      secondaries.forEach(function (s, i) {
        var sc = buildProductCard(s.p, s.label, s.cls, false);
        sc.style.animationDelay = ((i + 1) * 80) + 'ms';
        sc.classList.add('cm-card-enter');
        secRow.appendChild(sc);
      });
      secWrap.appendChild(secRow);
      chatEl.appendChild(secWrap);
    }

    if (Array.isArray(cards.tradeoffs) && cards.tradeoffs.length > 0) {
      var tfWrap = el('div', 'cm-tradeoffs');
      var tfLabel = el('div', 'cm-tf-label');
      tfLabel.textContent = '⚖️ Tradeoffs';
      tfWrap.appendChild(tfLabel);
      cards.tradeoffs.forEach(function (t) {
        var pill = el('div', 'cm-tradeoff-pill');
        pill.textContent = t;
        tfWrap.appendChild(pill);
      });
      chatEl.appendChild(tfWrap);
    }

    if (cards.inventory_warning && cards.best_overall) {
      var fbNote = el('div', 'cm-fallback-note');
      fbNote.textContent = 'Showing nearest in-stock alternatives within extended budget range.';
      chatEl.appendChild(fbNote);
    }
  }

  // ─── Comparison table ─────────────────────────────────────────────────────────
  function renderComparisonTable(comparison, chatEl) {
    if (!comparison || !Array.isArray(comparison.rows) || comparison.rows.length < 2) return;

    var wrap = el('div', 'cm-compare-wrap');

    if (comparison.deciding_factors && comparison.deciding_factors.length > 0) {
      var dfWrap = el('div', 'cm-decide-factors');
      var dfHead = el('div', 'cm-df-head');
      dfHead.textContent = 'Key deciding factors:';
      dfWrap.appendChild(dfHead);
      comparison.deciding_factors.forEach(function (d) {
        var item = el('div', 'cm-df-item');
        item.textContent = d;
        dfWrap.appendChild(item);
      });
      wrap.appendChild(dfWrap);
    }

    var tbl = el('table', 'cm-compare-table');
    var thead = document.createElement('thead');
    var hrow  = document.createElement('tr');
    var thFeature = el('th', 'cm-cf-label');
    thFeature.textContent = 'Feature';
    hrow.appendChild(thFeature);
    comparison.rows.forEach(function (r) {
      var th = document.createElement('th');
      th.innerHTML = '<span class="cm-ct-title">' + esc(r.title.slice(0, 40)) + '</span>';
      hrow.appendChild(th);
    });
    thead.appendChild(hrow);
    tbl.appendChild(thead);

    var tbody = document.createElement('tbody');
    var features = [
      { key: 'price_formatted',  label: '💰 Price',  winner: 'low' },
      { key: 'vendor',           label: '🏷️ Brand',  winner: 'none' },
      { key: 'in_stock',         label: '📦 Stock',  winner: 'bool',
        render: function (v) {
          return v
            ? '<span class="cm-ok">✓ In Stock</span>'
            : '<span class="cm-no">✗ Out of Stock</span>';
        }
      },
      { key: 'inventory', label: '🔢 Units', winner: 'high',
        render: function (v) { return (v > 0 ? v + ' units' : '—'); }
      }
    ];

    var specMeta = [
      { k: 'cpu',     label: '🔧 CPU' },
      { k: 'gpu',     label: '🎮 GPU' },
      { k: 'ram',     label: '💾 RAM' },
      { k: 'storage', label: '💿 Storage' },
      { k: 'screen',  label: '🖥️ Screen' }
    ];
    specMeta.forEach(function (sm) {
      if (comparison.rows.some(function (r) { return r.detected_specs && r.detected_specs[sm.k]; })) {
        features.push({
          key: '__spec_' + sm.k, label: sm.label, winner: 'none',
          render: function (v) { return v ? esc(v) : '—'; }
        });
      }
    });

    features.forEach(function (f) {
      var row  = document.createElement('tr');
      var tdLb = el('td', 'cm-cf-label');
      tdLb.textContent = f.label;
      row.appendChild(tdLb);

      var vals = comparison.rows.map(function (r) {
        if (f.key.startsWith('__spec_')) {
          var sk = f.key.replace('__spec_', '');
          return r.detected_specs ? r.detected_specs[sk] : null;
        }
        return r[f.key];
      });

      var winIdx = -1;
      if (f.winner === 'low') {
        var prices = vals.map(function (v) { return parseFloat(v) || Infinity; });
        winIdx = prices.indexOf(Math.min.apply(null, prices));
      } else if (f.winner === 'high') {
        var nums = vals.map(function (v) { return parseFloat(v) || 0; });
        winIdx = nums.indexOf(Math.max.apply(null, nums));
      } else if (f.winner === 'bool') {
        winIdx = vals.findIndex(function (v) { return !!v; });
      }

      vals.forEach(function (v, i) {
        var td = document.createElement('td');
        td.innerHTML = f.render ? f.render(v) : esc(v != null ? String(v) : '—');
        if (i === winIdx && f.winner !== 'none') td.classList.add('cm-winner');
        row.appendChild(td);
      });
      tbody.appendChild(row);
    });

    tbl.appendChild(tbody);

    var linkRow = document.createElement('tr');
    linkRow.className = 'cm-ct-links-row';
    var tdEmpty = el('td', 'cm-cf-label');
    tdEmpty.textContent = '🔗 View';
    linkRow.appendChild(tdEmpty);
    comparison.rows.forEach(function (r) {
      var td = document.createElement('td');
      td.innerHTML = '<a href="' + esc(r.url || '#') + '" target="_blank" rel="noopener" class="cm-ct-link">Open →</a>';
      linkRow.appendChild(td);
    });
    tbody.appendChild(linkRow);

    wrap.appendChild(tbl);
    chatEl.appendChild(wrap);
  }

  // ─── Suggestion chips ─────────────────────────────────────────────────────────
  function renderSuggestions(sugs, chatEl) {
    if (!sugs || sugs.length === 0) return;
    var wrap = el('div', 'cm-sugs');
    sugs.forEach(function (s) {
      var b = el('button', 'cm-sug');
      b.textContent = s;
      b.addEventListener('click', function () { cmAsk(s); });
      wrap.appendChild(b);
    });
    chatEl.appendChild(wrap);
  }

  // ─── addBotMsg ────────────────────────────────────────────────────────────────
  function addBotMsg(text, apiData, suggestions) {
    var isObj    = apiData && typeof apiData === 'object' && !Array.isArray(apiData);
    var products = isObj ? (apiData.products || []) : (Array.isArray(apiData) ? apiData : []);
    var recCards = isObj ? (apiData.recommendation_cards || null) : null;
    var comparison = isObj ? (apiData.comparison || null) : null;
    var fallback   = isObj ? (apiData.fallback || null) : null;

    var c   = chat();
    var rtl = isRTL(text);
    var uid = 'cm-bub-' + Date.now();

    var row = el('div', 'cm-mrow b' + (rtl ? ' rtl' : ''));
    row.innerHTML =
      '<div class="cm-mav" aria-hidden="true">CM</div>' +
      '<div class="cm-msg-wrap">' +
        '<div class="cm-bub" id="' + uid + '"></div>' +
        '<div class="cm-meta">' + ts() + '</div>' +
      '</div>';
    c.appendChild(row);
    c.scrollTop = c.scrollHeight;

    var bubEl = document.getElementById(uid);
    var htmlText = md(text);

    typewrite(bubEl, htmlText, function () {
      c.scrollTop = c.scrollHeight;

      if (recCards) {
        renderRecommendationCards(recCards, c);
      } else if (products.length > 0) {
        renderProductGrid(products.slice(0, 4), c);
      }

      if (fallback && Array.isArray(fallback.products) && fallback.products.length > 0) {
        var fbHead = el('div', 'cm-fallback-header');
        fbHead.textContent = '🔍 Nearby alternatives (extended search):';
        c.appendChild(fbHead);
        renderProductGrid(fallback.products.slice(0, 3), c);
      }

      if (comparison) {
        renderComparisonTable(comparison, c);
      }

      if (suggestions && suggestions.length > 0) {
        renderSuggestions(suggestions, c);
      }

      c.scrollTop = c.scrollHeight;
    });

    var live = document.getElementById('cm-live');
    if (live) live.textContent = String(text || '').slice(0, 200);
  }

  // ─── addUserMsg ───────────────────────────────────────────────────────────────
  function addUserMsg(text) {
    var c   = chat();
    var rtl = isRTL(text);
    var row = el('div', 'cm-mrow u' + (rtl ? ' rtl' : ''));
    row.innerHTML =
      '<div class="cm-mav user" aria-hidden="true">👤</div>' +
      '<div class="cm-msg-wrap">' +
        '<div class="cm-bub user">' + esc(text) + '</div>' +
        '<div class="cm-meta">' + ts() + '</div>' +
      '</div>';
    c.appendChild(row);
    c.scrollTop = c.scrollHeight;
  }

  // ─── Typing indicator ─────────────────────────────────────────────────────────
  function showTyping() {
    var c   = chat();
    var row = el('div', 'cm-mrow b');
    row.id  = 'cm-typ';
    row.innerHTML =
      '<div class="cm-mav" aria-hidden="true">CM</div>' +
      '<div class="cm-bub"><div class="cm-tdots" aria-label="AI is thinking">' +
        '<span></span><span></span><span></span>' +
      '</div></div>';
    c.appendChild(row);
    c.scrollTop = c.scrollHeight;
  }

  function removeTyping() {
    var t = document.getElementById('cm-typ');
    if (t) t.remove();
  }

  // ─── WhatsApp escalation ──────────────────────────────────────────────────────
  function showWhatsAppEscalation() {
    var c   = chat();
    var box = el('div', 'cm-escalate');
    box.innerHTML =
      '<p>🤝 Let me connect you with our team for instant help!</p>' +
      '<a href="' + WHATSAPP + '?text=' + encodeURIComponent('Hello CompuMe! I need help finding a product.') + '" ' +
         'target="_blank" rel="noopener" class="cm-wa-btn">' +
        '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>' +
        'Chat on WhatsApp' +
      '</a>';
    c.appendChild(box);
    c.scrollTop = c.scrollHeight;
  }

  // ─── Chat initialization ──────────────────────────────────────────────────────
  function initChat() {
    if (CM_MSGS.length > 0) { restoreChatUI(); return; }
    var ctx = getPageContext();
    var greeting, sugs;

    if (ctx && ctx.product && ctx.product.title) {
      greeting =
        'أهلاً! 👋 I can see you\'re looking at **' + ctx.product.title + '**' +
        (ctx.product.price ? ' (' + ctx.product.price + ')' : '') +
        '.\n\nWant more details, specs, or comparisons with alternatives? Ask in Arabic or English!';
      sugs = [
        'Tell me the full specs',
        'Compare with similar products',
        'Is this available now?',
        'هل هذا أفضل خيار في سعره؟'
      ];
    } else {
      greeting =
        'أهلاً بك في CompuMe! 👋\n\n' +
        'I\'m your AI shopping assistant with **live access** to 24,000+ products — local stock in Amman and USA imports shipped directly to you.\n\n' +
        'Ask me anything in **Arabic or English** — products, prices, specs, comparisons, or delivery!';
      sugs = [
        'Show me gaming laptops under 600 JOD',
        'أفضل لابتوب للدراسة بميزانية 400 د.أ',
        'What\'s the difference between local and USA imports?',
        'Build me a gaming PC under 1000 JOD'
      ];
    }
    addBotMsg(greeting, null, sugs);
  }

  function restoreChatUI() {
    var c = chat();
    c.innerHTML = '';
    CM_MSGS.forEach(function (m) {
      if (m.role === 'user') {
        addUserMsg(m.content);
      } else if (m.role === 'assistant') {
        var rtl = isRTL(m.content);
        var row = el('div', 'cm-mrow b' + (rtl ? ' rtl' : ''));
        row.innerHTML =
          '<div class="cm-mav" aria-hidden="true">CM</div>' +
          '<div class="cm-msg-wrap">' +
            '<div class="cm-bub">' + md(m.content) + '</div>' +
            '<div class="cm-meta">' + ts() + '</div>' +
          '</div>';
        c.appendChild(row);
        if (m._products && m._products.length > 0) {
          renderProductGrid(m._products, c);
        }
      }
    });
    var note = el('div', 'cm-resume-note');
    note.textContent = '— continuing your conversation —';
    c.appendChild(note);
    c.scrollTop = c.scrollHeight;
  }

  // ─── Smart follow-up suggestions ─────────────────────────────────────────────
  function generateSuggestions(userMsg, reply, products, intent, memory) {
    var sugs  = [];
    var msgL  = String(userMsg || '').toLowerCase();
    var hasP  = products && products.length > 0;
    memory    = memory || {};

    if (intent === 'compare') {
      sugs = ['Which one is better value?', 'What\'s the warranty difference?', 'Tell me the full specs of each'];
    } else if (intent === 'compatibility') {
      sugs = ['Show me compatible accessories', 'What other parts do I need?', 'Is this available locally?'];
    } else if (intent === 'support') {
      sugs = ['What\'s your return policy?', 'How fast is delivery to Amman?', 'Can I pick up in-store?'];
    } else if (hasP) {
      sugs.push('What\'s the warranty on this?');
      sugs.push('How fast is delivery?');
      if (products[0] && products[0].isUSA) {
        sugs.push('Are customs and taxes included?');
      } else {
        sugs.push('Can I pick it up from the Amman store?');
      }
    } else if (msgL.includes('laptop') || msgL.includes('لابتوب') || msgL.includes('notebook')) {
      sugs = ['What specs do I need for gaming?', 'Best laptops under 500 JOD', 'MacBook vs Windows — which is better?'];
    } else if (msgL.includes('headphone') || msgL.includes('سماعة') || msgL.includes('earphone')) {
      sugs = ['Sony vs Bose — which is better?', 'Best wireless earbuds under 80 JOD', 'Are these noise-cancelling?'];
    } else if (msgL.includes('gaming') || msgL.includes('game') || msgL.includes('ألعاب')) {
      sugs = ['Show me RTX gaming laptops', 'Best gaming mouse under 40 JOD', 'Build me a gaming PC under 1000 JOD'];
    } else if (msgL.includes('ship') || msgL.includes('deliver') || msgL.includes('توصيل')) {
      sugs = ['How fast is delivery in Amman?', 'Do you deliver to all of Jordan?', 'What about USA import delivery?'];
    } else if (msgL.includes('usa') || msgL.includes('import') || msgL.includes('أمريكا')) {
      sugs = ['Show me USA import laptops', 'Why buy a USA import?', 'Are customs really included?'];
    } else if (memory.budget_max_jod) {
      sugs = [
        'Show me the best option under ' + memory.budget_max_jod + ' JOD',
        'Is there anything better for ' + memory.budget_max_jod + ' JOD?',
        'What accessories fit my budget?'
      ];
    }

    return sugs.slice(0, 3);
  }

  // ─── FAB label toggle ─────────────────────────────────────────────────────────
  function updateFabState(open) {
    var fabIcon = document.getElementById('cm-fab-icon');
    var fabText = document.getElementById('cm-fab-text');
    if (!fabIcon || !fabText) return;
    if (open) {
      fabIcon.innerHTML = '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>';
      fabText.textContent = 'Close';
    } else {
      fabIcon.innerHTML = '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="13" y2="14"/>';
      fabText.textContent = 'Ask AI';
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────────
  window.cmToggle = function () {
    CM_OPEN = !CM_OPEN;
    var panel = document.getElementById('cm-panel');
    var fab   = document.getElementById('cm-fab');
    panel.className = CM_OPEN ? 'open' : '';
    updateFabState(CM_OPEN);
    if (CM_OPEN) {
      var unread = document.getElementById('cm-unread');
      if (unread) unread.style.display = 'none';
      fab.setAttribute('aria-expanded', 'true');
      loadHistory();
      cmTab('chat');
      if (CM_MSGS.length === 0) initChat();
      else restoreChatUI();
      setTimeout(function () {
        var txt = document.getElementById('cm-txt');
        if (txt) txt.focus();
      }, 250);
    } else {
      fab.setAttribute('aria-expanded', 'false');
    }
  };

  window.cmTab = function (t) {
    var tabs = { home: 'block', chat: 'flex' };
    Object.keys(tabs).forEach(function (k) {
      var pane = document.getElementById('cm-' + k);
      if (pane) pane.style.display = (k === t) ? tabs[k] : 'none';
      var tb = document.getElementById('cm-t-' + k);
      if (tb) {
        tb.className = 'cm-tab' + (k === t ? ' on' : '');
        tb.setAttribute('aria-selected', k === t ? 'true' : 'false');
      }
    });
  };

  window.cmAsk = function (text) {
    if (!CM_OPEN) cmToggle();
    cmTab('chat');
    if (CM_MSGS.length === 0) initChat();
    var txt = document.getElementById('cm-txt');
    if (txt) txt.value = text;
    cmSend();
  };

  window.cmSend = async function () {
    if (CM_BUSY) return;

    var now = Date.now();
    if (now - CM_LAST_SEND < 1500) return;
    CM_LAST_SEND = now;

    var txt     = document.getElementById('cm-txt');
    var sendBtn = document.getElementById('cm-send');
    var t       = txt ? txt.value.trim() : '';
    if (!t) return;

    txt.value = '';
    txt.style.height = 'auto';
    CM_BUSY = true;
    if (sendBtn) sendBtn.disabled = true;

    cmTab('chat');
    addUserMsg(t);
    CM_MSGS.push({ role: 'user', content: t });
    showTyping();

    var pageCtx = getPageContext();

    try {
      var apiMessages = [];
      var lastAssistant = null;
      for (var mi = CM_MSGS.length - 2; mi >= 0; mi--) {
        if (CM_MSGS[mi] && CM_MSGS[mi].role === 'assistant') {
          lastAssistant = CM_MSGS[mi];
          break;
        }
      }
      if (lastAssistant) {
        var lastUserIdx = CM_MSGS.length - 2;
        if (lastUserIdx >= 0 && CM_MSGS[lastUserIdx] && CM_MSGS[lastUserIdx].role === 'user') {
          apiMessages.push({ role: 'user', content: String(CM_MSGS[lastUserIdx].content || '') });
        }
        apiMessages.push({ role: 'assistant', content: String(lastAssistant.content || '').slice(0, 2000) });
      }
      apiMessages.push({ role: 'user', content: String(t) });
      var res = await fetch(WORKER_URL + '/chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages })
      });

      removeTyping();

      if (res.status === 429) {
        addBotMsg('You\'re sending messages too fast — please wait a moment! ⏱️', null, null);
        CM_MSGS.pop();
        CM_BUSY = false;
        if (sendBtn) sendBtn.disabled = false;
        return;
      }

      if (!res.ok) throw new Error('HTTP ' + res.status);

      var data = await res.json();

      if (data.error && !data.response) {
        CM_ERRORS++;
        addBotMsg(data.message || data.error, null, null);
        if (CM_ERRORS >= 2) showWhatsAppEscalation();
        CM_MSGS.pop();
      } else {
        CM_ERRORS = 0;
        var reply = data.response || 'No response received.';

        if (data.memory)  CM_MEMORY = data.memory;
        if (data.intent)  CM_INTENT = data.intent;

        var allCards = (data.products || []).filter(function(p) {
          return p.available !== false && p.in_stock !== false;
        });

        function cleanProductUrl(url) {
          return decodeURIComponent(String(url || '')).split('?')[0].split('#')[0].replace(/\/$/, '').toLowerCase();
        }
        var urlsInReply = [];
        var urlRe = /https:\/\/compume\.jo\/products\/([^\s)\]"'<]+)/g;
        var urlMatch;
        while ((urlMatch = urlRe.exec(reply)) !== null) {
          var cleanUrl = cleanProductUrl(urlMatch[0]);
          if (urlsInReply.indexOf(cleanUrl) === -1) urlsInReply.push(cleanUrl);
        }

        var filteredProducts;
        if (urlsInReply.length > 0) {
          filteredProducts = allCards.filter(function(p) {
            var pUrl = cleanProductUrl(p.url || '');
            return urlsInReply.some(function(u) { return pUrl === u; });
          });
        }

        if (!filteredProducts || filteredProducts.length === 0) {
          var tLow2 = t.toLowerCase();
          if (/laptop|notebook|لابتوب/.test(tLow2)) {
            filteredProducts = allCards.filter(function(p) {
              return !/(desktop|tower|aio|all.in.one|prodesk|elitedesk|optiplex|docking|hub|dock)/i.test(p.title || '');
            });
          } else {
            var qWords = tLow2.split(' ').filter(function(w) { return w.length > 3; });
            var kwCards = allCards.filter(function(p) {
              var ptitle = (p.title || '').toLowerCase();
              return qWords.some(function(w) { return ptitle.indexOf(w) !== -1; });
            });
            filteredProducts = kwCards.length > 0 ? kwCards : [];
          }
        }

        var displayData = Object.assign({}, data, { products: filteredProducts });
        var sugs = generateSuggestions(t, reply, filteredProducts, data.intent, data.memory);
        addBotMsg(reply, displayData, sugs);

        CM_MSGS.push({
          role: 'assistant',
          content: reply,
          _products: data.products || []
        });
        saveHistory();
      }

    } catch (ex) {
      removeTyping();
      CM_ERRORS++;
      addBotMsg(
        'Connection issue — please check your internet or reach us on WhatsApp: +962 79 800 0180 🙏',
        null, null
      );
      if (CM_ERRORS >= 2) showWhatsAppEscalation();
      CM_MSGS.pop();
    }

    CM_BUSY  = false;
    if (sendBtn) sendBtn.disabled = false;
    var txtEl = document.getElementById('cm-txt');
    if (txtEl) txtEl.focus();
  };

  // ─── Unread badge on first visit ──────────────────────────────────────────────
  if (!localStorage.getItem(LS_KEY)) {
    setTimeout(function () {
      var u = document.getElementById('cm-unread');
      if (u) u.style.display = 'flex';
    }, 3500);
  }

  // ─── DOM ready: event listeners ───────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    var txt     = document.getElementById('cm-txt');
    var panel   = document.getElementById('cm-panel');

    if (txt) {
      txt.addEventListener('input', function () {
        this.style.height = 'auto';
        this.style.height = Math.min(this.scrollHeight, 100) + 'px';
      });
      txt.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          cmSend();
        }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && CM_OPEN) cmToggle();
    });

    document.addEventListener('click', function (e) {
      if (!CM_OPEN) return;
      var fab = document.getElementById('cm-fab');
      if (panel && !panel.contains(e.target) && fab && !fab.contains(e.target)) {
        cmToggle();
      }
    });

    if (panel) {
      panel.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var focusable = panel.querySelectorAll(
          'button:not([disabled]), input, textarea, a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last  = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      });
    }
  });

})();